import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const SCROLL_DURATION_MS = 560;
const FIRST_MESSAGE_TOP_OFFSET_PX = 16;
const FOLLOW_UP_MESSAGE_TOP_OFFSET_PX = 24;
const MIN_SCROLL_DELTA_PX = 1;
const SPACER_UPDATE_THRESHOLD_PX = 1;
const MOBILE_BREAKPOINT_PX = 992;
const SMALL_VIEWPORT_BREAKPOINT_PX = 576;
const MAIN_HEADER_HEIGHT_SMALL_PX = 52; // Below 576px
const MAIN_HEADER_HEIGHT_LARGE_PX = 88; // 576px and above
const CHAT_HEADER_HEIGHT_PX = 48;
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const isMobileViewport = (): boolean => {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX;
};

/**
 * Returns the main header height based on viewport width.
 * Below 576px: 52px, 576px and above: 88px
 */
const getMainHeaderHeight = (): number => {
  if (typeof window === 'undefined') return MAIN_HEADER_HEIGHT_SMALL_PX;
  return window.innerWidth >= SMALL_VIEWPORT_BREAKPOINT_PX ? MAIN_HEADER_HEIGHT_LARGE_PX : MAIN_HEADER_HEIGHT_SMALL_PX;
};

/**
 * Returns the total height of sticky/fixed headers on mobile.
 * When main header is sticky, both headers are fixed and take up space.
 */
const getMobileStickyHeaderOffset = (): number => {
  if (typeof window === 'undefined' || !isMobileViewport()) return 0;

  const mainHeader = document.querySelector('[data-component-name="o-common-static-navigationcontainer"]');
  const isMainHeaderSticky = mainHeader?.classList.contains('header-sticky');

  if (isMainHeaderSticky) {
    // Both main header and chat header are fixed
    return getMainHeaderHeight() + CHAT_HEADER_HEIGHT_PX;
  }

  // Only chat header is sticky at top: 0
  return CHAT_HEADER_HEIGHT_PX;
};

interface ActiveMessageScrollState {
  pendingMessageId: string | null;
  activeMessageId: string | null;
  isFollowUp: boolean;
}

interface UseActiveMessageScrollParams {
  messages: Array<{ id: string; isUser: boolean }>;
  isProcessing: boolean;
  /** Align ref cleanup with StatusIndicator removal (SSE defers status idle ~500ms after complete). */
  agentStatus: string;
}

interface UseActiveMessageScrollResult {
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  setMessageNodeRef: (messageId: string, node: HTMLDivElement | null) => void;
  prepareActiveMessageScroll: (messageId: string, isFollowUp: boolean) => void;
  activeMessageSpacerHeight: number;
}

/**
 * Keeps the active user message near the top while assistant response streams below.
 */
export const useActiveMessageScroll = ({
  messages,
  isProcessing,
  agentStatus,
}: UseActiveMessageScrollParams): UseActiveMessageScrollResult => {
  // Layout identity without thumbFeedback — used only by the idle spacer effect below. Thumb updates
  // replace `messages` but don't change layout; without this, that effect re-runs and syncScroll()
  // jumps the viewport when a spacer is present (short chats with spacer 0 bail out earlier).
  const messagesScrollLayoutKey = useMemo(
    () =>
      messages
        .map(m => {
          const ext = m as { content?: string; uiType?: string };
          return `${m.id}\u0000${m.isUser ? 1 : 0}\u0000${(ext.content || '').length}\u0000${ext.uiType || ''}`;
        })
        .join('\u001f'),
    [messages]
  );

  // Scrollable chat container and each message node, used to compute exact scroll targets.
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageNodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Tracks which user message should drive the current "scroll to top" behavior.
  const activeMessageScrollRef = useRef<ActiveMessageScrollState>({
    pendingMessageId: null,
    activeMessageId: null,
    isFollowUp: false,
  });
  const scrollAnimationFrameRef = useRef<number | null>(null);
  // Temporary spacer that creates enough room to keep active message pinned near top.
  const [activeMessageSpacerHeight, setActiveMessageSpacerHeight] = useState(0);
  // Mirrors state for layout math so getRequiredSpacerHeight does not need state in its
  // dependency list, and always reads the spacer height from the last committed render.
  const activeMessageSpacerHeightRef = useRef(0);
  activeMessageSpacerHeightRef.current = activeMessageSpacerHeight;

  const runAnimatedScroll = useCallback(
    (
      container: HTMLDivElement,
      targetTop: number,
      duration: number,
      frameRef: React.MutableRefObject<number | null>
    ) => {
      const isMobile = isMobileViewport();
      const startTop = isMobile ? window.scrollY || document.documentElement.scrollTop : container.scrollTop;
      const delta = targetTop - startTop;
      if (Math.abs(delta) < MIN_SCROLL_DELTA_PX) {
        if (frameRef.current !== null) {
          cancelAnimationFrame(frameRef.current);
          frameRef.current = null;
        }
        if (isMobile) {
          window.scrollTo({ top: targetTop });
        } else {
          container.scrollTop = targetTop;
        }
        return false;
      }

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }

      const startTs = performance.now();
      const tick = (now: number) => {
        const elapsed = now - startTs;
        const progress = Math.min(1, elapsed / duration);
        const eased = easeInOutCubic(progress);
        const newScrollTop = startTop + delta * eased;
        if (isMobile) {
          window.scrollTo({ top: newScrollTop });
        } else {
          container.scrollTop = newScrollTop;
        }
        if (progress < 1) {
          frameRef.current = requestAnimationFrame(tick);
        } else {
          frameRef.current = null;
        }
      };

      frameRef.current = requestAnimationFrame(tick);
      return true;
    },
    []
  );

  const animateScrollToActiveMessage = useCallback(
    (container: HTMLDivElement, targetTop: number) => {
      runAnimatedScroll(container, targetTop, SCROLL_DURATION_MS, scrollAnimationFrameRef);
    },
    [runAnimatedScroll]
  );

  const getTopOffset = useCallback((isFollowUp: boolean) => {
    const baseOffset = isFollowUp ? FOLLOW_UP_MESSAGE_TOP_OFFSET_PX : FIRST_MESSAGE_TOP_OFFSET_PX;
    // On mobile, add the sticky header height so content appears below the fixed headers
    const stickyHeaderOffset = getMobileStickyHeaderOffset();
    return baseOffset + stickyHeaderOffset;
  }, []);

  const getMessageTopInContainer = useCallback((container: HTMLDivElement, messageNode: HTMLDivElement) => {
    if (isMobileViewport()) {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      return messageNode.getBoundingClientRect().top + scrollTop;
    }
    return messageNode.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
  }, []);

  const getRequiredSpacerHeight = useCallback(
    (container: HTMLDivElement, messageNode: HTMLDivElement, topOffset: number) => {
      const targetTop = Math.max(0, getMessageTopInContainer(container, messageNode) - topOffset);

      // If we don't need to scroll at all, no spacer is required to achieve targetTop=0
      if (targetTop === 0) return 0;

      const spacerPx = activeMessageSpacerHeightRef.current;

      if (isMobileViewport()) {
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = window.innerHeight;
        const scrollHeightWithoutSpacer = Math.max(0, scrollHeight - spacerPx);
        const maxScrollableWithoutSpacer = Math.max(0, scrollHeightWithoutSpacer - clientHeight);
        return Math.max(0, Math.round(targetTop - maxScrollableWithoutSpacer));
      }

      const scrollHeightWithoutSpacer = Math.max(0, container.scrollHeight - spacerPx);
      const maxScrollableWithoutSpacer = Math.max(0, scrollHeightWithoutSpacer - container.clientHeight);
      // Round (not ceil) so subpixel layout + streaming re-renders do not flip between
      // two integer spacer values and trigger an update depth loop.
      return Math.max(0, Math.round(targetTop - maxScrollableWithoutSpacer));
    },
    [getMessageTopInContainer]
  );

  const resolveActiveMessageMetrics = useCallback(
    (messageId: string, isFollowUp: boolean) => {
      const container = messagesContainerRef.current;
      const messageNode = messageNodeRefs.current[messageId];
      if (!container || !messageNode) return null;

      const topOffset = getTopOffset(isFollowUp);
      const targetTop = Math.max(0, getMessageTopInContainer(container, messageNode) - topOffset);
      const requiredSpacer = getRequiredSpacerHeight(container, messageNode, topOffset);

      return { container, targetTop, requiredSpacer };
    },
    [getMessageTopInContainer, getRequiredSpacerHeight, getTopOffset]
  );

  const updateSpacerIfNeeded = useCallback((nextSpacerHeight: number) => {
    setActiveMessageSpacerHeight(prev => {
      if (Math.abs(nextSpacerHeight - prev) <= SPACER_UPDATE_THRESHOLD_PX) {
        return prev;
      }
      return nextSpacerHeight;
    });
  }, []);

  // One-shot: after user sends, scroll that specific message to target offset.
  useLayoutEffect(() => {
    const pendingMessageId = activeMessageScrollRef.current.pendingMessageId;
    if (!pendingMessageId) return;

    const metrics = resolveActiveMessageMetrics(pendingMessageId, activeMessageScrollRef.current.isFollowUp);
    if (!metrics) return;

    updateSpacerIfNeeded(metrics.requiredSpacer);
    animateScrollToActiveMessage(metrics.container, metrics.targetTop);
    activeMessageScrollRef.current.pendingMessageId = null;
  }, [messages, animateScrollToActiveMessage, resolveActiveMessageMetrics, updateSpacerIfNeeded]);

  // While assistant response streams, continuously trim spacer to the minimum required.
  useLayoutEffect(() => {
    const activeMessageId = activeMessageScrollRef.current.activeMessageId;
    if (!activeMessageId) return;
    // While the "pin user message" scroll animation runs, scrollTop changes every frame.
    // Recomputing spacer against moving scrollTop can oscillate requiredSpacer and hit
    // React's max update depth (especially with short viewports and long prompts).
    if (scrollAnimationFrameRef.current !== null) return;

    const metrics = resolveActiveMessageMetrics(activeMessageId, activeMessageScrollRef.current.isFollowUp);
    if (!metrics) return;

    updateSpacerIfNeeded(metrics.requiredSpacer);
  }, [messages, isProcessing, agentStatus, resolveActiveMessageMetrics, updateSpacerIfNeeded]);

  // When pin refs are cleared (idle) but spacer remains, there is no activeMessageId — e.g. after
  // "Try again" (retry skips prepareActiveMessageScroll). Then the streaming spacer effect never
  // runs: removing the error shrinks the stack; adding a new error grows it — both need the same
  // recomputation from the last user message or spacer stays too large on the second failure.
  // Depends on messagesScrollLayoutKey so thumb-only store updates do not call syncScroll() here
  // (that jump only surfaces when activeMessageSpacerHeight > 0).
  useLayoutEffect(() => {
    if (activeMessageScrollRef.current.activeMessageId) return;
    if (activeMessageSpacerHeightRef.current === 0) return;

    let lastUserId: string | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isUser) {
        lastUserId = messages[i].id;
        break;
      }
    }
    if (!lastUserId) return;

    const userTurnCount = messages.filter(m => m.isUser).length;
    const isFollowUp = userTurnCount > 1;
    const metrics = resolveActiveMessageMetrics(lastUserId, isFollowUp);
    if (!metrics) return;

    const prevSpacer = activeMessageSpacerHeightRef.current;
    const nextSpacer = metrics.requiredSpacer;
    const spacerChanged = Math.abs(nextSpacer - prevSpacer) > SPACER_UPDATE_THRESHOLD_PX;
    // Same thresholded updates as streaming path — avoids spacer feedback loops when idle.
    updateSpacerIfNeeded(nextSpacer);

    const pinId = lastUserId;
    const pinFollowUp = isFollowUp;
    const syncScroll = () => {
      const m = resolveActiveMessageMetrics(pinId, pinFollowUp);
      if (!m) return;

      if (isMobileViewport()) {
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = window.innerHeight;
        const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
        window.scrollTo({ top: Math.min(Math.max(0, m.targetTop), maxScrollTop) });
      } else {
        const maxScrollTop = Math.max(0, m.container.scrollHeight - m.container.clientHeight);
        m.container.scrollTop = Math.min(Math.max(0, m.targetTop), maxScrollTop);
      }
    };

    if (spacerChanged) {
      requestAnimationFrame(() => {
        requestAnimationFrame(syncScroll);
      });
    } else {
      syncScroll();
    }
  }, [messagesScrollLayoutKey, isProcessing, agentStatus, resolveActiveMessageMetrics, updateSpacerIfNeeded]);

  // Reset spacer if chat is cleared
  useEffect(() => {
    if (messages.length === 0 && activeMessageSpacerHeight !== 0) {
      setActiveMessageSpacerHeight(0);
    }
  }, [messages.length, activeMessageSpacerHeight]);

  // End-of-request cleanup so next request starts from a clean state.
  useEffect(() => {
    if (!isProcessing && agentStatus === 'idle') {
      // Do NOT reset activeMessageSpacerHeight here.
      // Leave it so the previous message stays pinned at the top.
      // If the next response is longer, it will naturally shrink the spacer.
      activeMessageScrollRef.current.pendingMessageId = null;
      activeMessageScrollRef.current.activeMessageId = null;
      activeMessageScrollRef.current.isFollowUp = false;
    }
  }, [isProcessing, agentStatus]);

  useEffect(() => {
    return () => {
      if (scrollAnimationFrameRef.current !== null) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
        scrollAnimationFrameRef.current = null;
      }
    };
  }, []);

  // Called right before appending user message to arm the next scroll cycle.
  const prepareActiveMessageScroll = useCallback((messageId: string, isFollowUp: boolean) => {
    activeMessageScrollRef.current.isFollowUp = isFollowUp;
    activeMessageScrollRef.current.activeMessageId = messageId;
    activeMessageScrollRef.current.pendingMessageId = messageId;
  }, []);

  const setMessageNodeRef = useCallback((messageId: string, node: HTMLDivElement | null) => {
    messageNodeRefs.current[messageId] = node;
  }, []);

  return {
    messagesContainerRef,
    setMessageNodeRef,
    prepareActiveMessageScroll,
    activeMessageSpacerHeight,
  };
};
